import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Checkbox,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon, ExpandMore as ExpandMoreIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

interface Permission {
  id: string;
  name: string;
  code: string;
  module: string;
  description: string;
}

interface RoleFormState {
  name: string;
  code: string;
  description: string;
  hierarchy: number;
  roleType: 'ADMIN' | 'STAFF';
  enabled: boolean;
  permissions: string[]; // Permission codes
}

const RoleForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [formData, setFormData] = useState<RoleFormState>({
    name: '',
    code: '',
    description: '',
    hierarchy: 10,
    roleType: 'STAFF',
    enabled: true,
    permissions: [],
  });

  const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPermissions();
    if (isEdit) {
      fetchRoleDetails();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchPermissions = async () => {
    try {
      const response = await axios.get('/system-settings/roles/permissions');
      setAvailablePermissions(response.data);
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError('Failed to load permissions');
    }
  };

  const fetchRoleDetails = async () => {
    try {
      const response = await axios.get(`/system-settings/roles/${id}`);
      const role = response.data;
      
      setFormData({
        name: role.name,
        code: role.code,
        description: role.description || '',
        hierarchy: role.hierarchy,
        roleType: role.hierarchy <= 3 ? 'ADMIN' : 'STAFF',
        enabled: role.isActive,
        permissions: role.permissions.map((p: any) => p.permission.code),
      });
      setLoading(false);
    } catch (err) {
      console.error('Error fetching role details:', err);
      setError('Failed to load role details');
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const normalizedName = formData.name
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

      const resolvedCode = isEdit
        ? formData.code
        : `${formData.roleType}_${normalizedName || 'ROLE'}`;

      const resolvedHierarchy = formData.roleType === 'ADMIN' ? 3 : 4;

      const payload = {
        name: formData.name,
        code: resolvedCode,
        description: formData.description,
        hierarchy: resolvedHierarchy,
        enabled: formData.enabled,
        permissions: formData.permissions,
      };

      if (isEdit) {
        await axios.put(`/system-settings/roles/${id}`, payload);
      } else {
        await axios.post('/system-settings/roles', payload);
      }
      navigate('/system-settings/user-roles/roles');
    } catch (err: any) {
      console.error('Error saving role:', err);
      setError(err.response?.data?.message || 'Failed to save role');
    }
  };

  const handlePermissionChange = (code: string) => {
    setFormData(prev => {
      const permission = availablePermissions.find(p => p.code === code);
      if (!permission) return prev;

      const isChecking = !prev.permissions.includes(code);
      let newPermissions = [...prev.permissions];

      if (isChecking) {
        newPermissions.push(code);
        // Auto-check VIEW if checking any other permission
        if (!code.endsWith('_VIEW')) {
           const viewPerm = availablePermissions.find(p => p.module === permission.module && p.code.endsWith('_VIEW'));
           if (viewPerm && !newPermissions.includes(viewPerm.code)) {
             newPermissions.push(viewPerm.code);
           }
        }
      } else {
        newPermissions = newPermissions.filter(p => p !== code);
        // Auto-uncheck all if unchecking VIEW
        if (code.endsWith('_VIEW')) {
           const modulePerms = availablePermissions.filter(p => p.module === permission.module);
           const otherCodes = modulePerms.map(p => p.code).filter(c => c !== code);
           newPermissions = newPermissions.filter(p => !otherCodes.includes(p));
        }
      }
      return { ...prev, permissions: newPermissions };
    });
  };

  const handleViewOnly = (e: React.MouseEvent, modulePermissions: Permission[]) => {
    e.stopPropagation();
    const viewPerm = modulePermissions.find(p => p.code.endsWith('_VIEW'));
    if (!viewPerm) return;

    setFormData(prev => {
      let newPermissions = [...prev.permissions];
      const otherCodes = modulePermissions.map(p => p.code).filter(c => c !== viewPerm.code);
      newPermissions = newPermissions.filter(p => !otherCodes.includes(p));
      if (!newPermissions.includes(viewPerm.code)) {
        newPermissions.push(viewPerm.code);
      }
      return { ...prev, permissions: newPermissions };
    });
  };

  const handleModuleToggle = (moduleName: string, modulePermissions: Permission[]) => {
    setFormData(prev => {
      const allSelected = modulePermissions.every(p => prev.permissions.includes(p.code));
      let newPermissions = [...prev.permissions];
      
      if (allSelected) {
        // Deselect all
        newPermissions = newPermissions.filter(code => 
          !modulePermissions.find(p => p.code === code)
        );
      } else {
        // Select all
        const toAdd = modulePermissions
          .map(p => p.code)
          .filter(code => !newPermissions.includes(code));
        newPermissions = [...newPermissions, ...toAdd];
      }
      
      return { ...prev, permissions: newPermissions };
    });
  };

  // Group permissions by module
  const permissionsByModule = availablePermissions.reduce((acc, curr) => {
    if (!acc[curr.module]) {
      acc[curr.module] = [];
    }
    acc[curr.module].push(curr);
    return acc;
  }, {} as Record<string, Permission[]>);

  if (loading && isEdit) {
    return <MainLayout><Container><Typography>Loading...</Typography></Container></MainLayout>;
  }

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/system-settings/user-roles/roles')}>
            Back
          </Button>
          <Typography variant="h4">
            {isEdit ? 'Edit Role' : 'Create New Role'}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Basic Information</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Role Name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormGroup>
                  <TextField
                    select
                    fullWidth
                    label="Role Type"
                    value={formData.roleType}
                    onChange={(e) => setFormData({ ...formData, roleType: e.target.value as 'ADMIN' | 'STAFF' })}
                    helperText="Designation will be grouped under this role type"
                  >
                    <MenuItem value="ADMIN">ADMIN</MenuItem>
                    <MenuItem value="STAFF">STAFF</MenuItem>
                  </TextField>
                </FormGroup>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enabled}
                      onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={formData.enabled ? 'Active' : 'Inactive'}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Permissions</Typography>
              <Typography variant="body2" color="textSecondary">
                Selected: {formData.permissions.length}
              </Typography>
            </Box>
            
            <Divider sx={{ mb: 2 }} />

            {Object.entries(permissionsByModule).map(([module, permissions]) => {
              const allSelected = permissions.every(p => formData.permissions.includes(p.code));
              const someSelected = permissions.some(p => formData.permissions.includes(p.code));
              
              return (
                <Accordion key={module} defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between', pr: 2 }}>
                        <FormControlLabel
                        aria-label="Acknowledge"
                        onClick={(event) => event.stopPropagation()}
                        onFocus={(event) => event.stopPropagation()}
                        control={
                            <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected && !allSelected}
                            onChange={() => handleModuleToggle(module, permissions)}
                            />
                        }
                        label={<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{module}</Typography>}
                        />
                        <Button 
                            size="small" 
                            startIcon={<VisibilityIcon />} 
                            onClick={(e) => handleViewOnly(e, permissions)}
                            variant="outlined"
                            sx={{ ml: 2 }}
                        >
                            View Only
                        </Button>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {permissions.map((permission) => {
                          // Use the database name field which has proper formatting
                          // Fall back to formatted code if name is not available
                          const getPermissionLabel = () => {
                            if (permission.name) return permission.name;
                            // Format code: STUDENT_CREATE -> Student Create
                            const parts = permission.code.split('_');
                            return parts
                              .map(p => p.charAt(0) + p.slice(1).toLowerCase())
                              .join(' ');
                          };
                          const permLabel = getPermissionLabel();
                          return (
                        <Grid item xs={12} sm={6} md={3} key={permission.id}>
                          <Paper variant="outlined" sx={{ p: 1, height: '100%' }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.permissions.includes(permission.code)}
                                  onChange={() => handlePermissionChange(permission.code)}
                                  size="small"
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {permLabel}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1.2, mt: 0.5 }}>
                                    {permission.code}
                                                                    {permission.description && (
                                                                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1.2, mt: 0.25, fontStyle: 'italic' }}>
                                                                        {permission.description}
                                                                      </Typography>
                                                                    )}
                                  </Typography>
                                </Box>
                              }
                              sx={{ alignItems: 'flex-start', ml: 0, width: '100%' }}
                            />
                          </Paper>
                        </Grid>
                      )})}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/system-settings/user-roles/roles')}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" startIcon={<SaveIcon />} size="large">
              Save Role
            </Button>
          </Box>
        </form>
      </Container>
    </MainLayout>
  );
};

export default RoleForm;
